import { useState } from "react";
import { MessageCircle, Send, CheckCircle2, Mail } from "lucide-react";
import { SITE, waLink, waMessageFor } from "@/config/site";
import { trackGenerateLead, trackWhatsappClick } from "@/lib/analytics";

const DESTINATION_OPTIONS = ["Andaman", "Goa", "Rajasthan", "Leh-Ladakh", "Other"];

const buildWhatsappMessage = (form) => {
  return [
    `Hi Deep Dive Trips, I'd like to plan a trip.`,
    ``,
    `Name: ${form.name}`,
    `Phone: ${form.phone}`,
    `Email: ${form.email}`,
    `Destination: ${form.destination}`,
    `Travel dates: ${form.travel_dates}`,
    `Travelers: ${form.travelers}`,
    form.message ? `Message: ${form.message}` : null,
  ]
    .filter(Boolean)
    .join("\n");
};

const buildMailto = (form) => {
  const subject = `New trip enquiry — ${form.destination} — ${form.name}`;
  const body = buildWhatsappMessage(form);
  return `mailto:${SITE.enquiryEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

// Fire-and-forget POST to Web3Forms so a copy of every enquiry lands in
// SITE.enquiryEmail without needing a backend. Silently no-ops if the
// access key hasn't been configured yet.
const sendToWeb3Forms = async (form, sourcePage) => {
  const key = SITE.web3formsAccessKey;
  if (!key || key.startsWith("REPLACE_ME")) return { ok: false, skipped: true };
  try {
    const payload = {
      access_key: key,
      subject: `New enquiry — ${form.destination} — ${form.name}`,
      from_name: `${SITE.name} website`,
      to_email: SITE.enquiryEmail,
      name: form.name,
      phone: form.phone,
      email: form.email,
      destination: form.destination,
      travel_dates: form.travel_dates,
      travelers: form.travelers,
      message: form.message || "(no message)",
      source_page: sourcePage || "",
      submitted_at: new Date().toISOString(),
    };
    const res = await fetch("https://api.web3forms.com/submit", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(payload),
    });
    return { ok: res.ok };
  } catch (_) {
    return { ok: false };
  }
};

export const EnquiryForm = ({ dark = false, defaultDestination = "", sourcePage = "", compact = false }) => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    destination: defaultDestination || "Andaman",
    travel_dates: "",
    travelers: "2",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Required";
    if (!form.phone.trim() || form.phone.trim().length < 6) errs.phone = "Please enter a valid phone";
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) errs.email = "Please enter a valid email";
    if (!form.travel_dates.trim()) errs.travel_dates = "Required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    trackGenerateLead(form.destination, sourcePage || "enquiry-form");
    // 1. Send a copy of the enquiry to the inbox via Web3Forms (no-op if key missing)
    sendToWeb3Forms(form, sourcePage);
    // 2. Open WhatsApp in a new tab with full details as the message
    const url = waLink(buildWhatsappMessage(form));
    window.open(url, "_blank", "noopener,noreferrer");
    setSubmitted(true);
  };

  const openMail = () => {
    if (!validate()) return;
    window.location.href = buildMailto(form);
  };

  // ------- palettes -------
  const wrapper = dark
    ? "bg-ocean-light/60 backdrop-blur-sm border border-white/10 text-white"
    : "bg-white border border-sand-200 text-ink";
  const labelCls = dark ? "text-white/60" : "text-ink-muted";
  const inputCls = dark
    ? "w-full bg-transparent border-b border-white/25 focus:border-sunset py-3 outline-none text-white placeholder:text-white/40 transition-colors"
    : "w-full bg-transparent border-b border-sand-300 focus:border-sunset py-3 outline-none text-ink placeholder:text-ink-muted/50 transition-colors";
  const err = "text-xs text-sunset mt-1";

  if (submitted) {
    return (
      <div className={`p-10 md:p-14 ${wrapper}`} data-testid="enquiry-thankyou">
        <CheckCircle2 size={48} className="text-sunset mb-6" strokeWidth={1.5} />
        <h3 className="font-heading text-3xl md:text-4xl mb-4">Thanks — we're on it.</h3>
        <p className={`text-base mb-8 max-w-lg ${dark ? "text-white/70" : "text-ink-muted"}`}>
          Your details are in — we've opened a WhatsApp draft to make this instant.
          If it didn't open, tap the button below. A travel expert typically replies within a few
          working hours.
        </p>
        <div className="flex flex-wrap gap-4">
          <a
            href={waLink(buildWhatsappMessage(form))}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="thankyou-whatsapp-followup"
            onClick={() => trackWhatsappClick("thankyou-followup")}
            className="btn-primary"
          >
            <MessageCircle size={18} /> Continue on WhatsApp
          </a>
          <button
            type="button"
            data-testid="thankyou-reset"
            onClick={() => setSubmitted(false)}
            className={dark ? "btn-secondary" : "btn-ghost-dark"}
          >
            Send another enquiry
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={`p-8 md:p-12 ${wrapper}`}
      data-testid="enquiry-form"
    >
      {!compact && (
        <div className="mb-8">
          <p className="overline mb-3">Plan your trip</p>
          <h3 className="font-heading text-3xl md:text-4xl leading-tight">
            Tell us where you'd like to go.
          </h3>
          <p className={`mt-3 text-sm ${dark ? "text-white/60" : "text-ink-muted"}`}>
            No payment on the site. We reply on WhatsApp within a few hours.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="enquiry-name-input" className={`block text-xs uppercase tracking-widest mb-2 ${labelCls}`}>Full Name</label>
          <input
            id="enquiry-name-input"
            type="text"
            value={form.name}
            onChange={set("name")}
            data-testid="enquiry-name"
            className={inputCls}
            placeholder="Priya Sharma"
          />
          {errors.name && <p className={err}>{errors.name}</p>}
        </div>
        <div>
          <label htmlFor="enquiry-phone-input" className={`block text-xs uppercase tracking-widest mb-2 ${labelCls}`}>Phone</label>
          <input
            id="enquiry-phone-input"
            type="tel"
            value={form.phone}
            onChange={set("phone")}
            data-testid="enquiry-phone"
            className={inputCls}
            placeholder="+91 98xxxxxxxx"
          />
          {errors.phone && <p className={err}>{errors.phone}</p>}
        </div>
        <div>
          <label htmlFor="enquiry-email-input" className={`block text-xs uppercase tracking-widest mb-2 ${labelCls}`}>Email</label>
          <input
            id="enquiry-email-input"
            type="email"
            value={form.email}
            onChange={set("email")}
            data-testid="enquiry-email"
            className={inputCls}
            placeholder="you@email.com"
          />
          {errors.email && <p className={err}>{errors.email}</p>}
        </div>
        <div>
          <label htmlFor="enquiry-destination-select" className={`block text-xs uppercase tracking-widest mb-2 ${labelCls}`}>Destination</label>
          <select
            id="enquiry-destination-select"
            value={form.destination}
            onChange={set("destination")}
            data-testid="enquiry-destination"
            className={`${inputCls} appearance-none`}
          >
            {DESTINATION_OPTIONS.map((d) => (
              <option key={d} value={d} className="text-ink">{d}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="enquiry-dates-input" className={`block text-xs uppercase tracking-widest mb-2 ${labelCls}`}>Travel Dates</label>
          <input
            id="enquiry-dates-input"
            type="text"
            value={form.travel_dates}
            onChange={set("travel_dates")}
            data-testid="enquiry-dates"
            className={inputCls}
            placeholder="e.g. 10–17 Feb 2026 (flexible)"
          />
          {errors.travel_dates && <p className={err}>{errors.travel_dates}</p>}
        </div>
        <div>
          <label htmlFor="enquiry-travelers-input" className={`block text-xs uppercase tracking-widest mb-2 ${labelCls}`}>Number of Travelers</label>
          <input
            id="enquiry-travelers-input"
            type="text"
            value={form.travelers}
            onChange={set("travelers")}
            data-testid="enquiry-travelers"
            className={inputCls}
            placeholder="2 adults + 1 child"
          />
        </div>
        <div className="md:col-span-2">
          <label htmlFor="enquiry-message-input" className={`block text-xs uppercase tracking-widest mb-2 ${labelCls}`}>Message</label>
          <textarea
            id="enquiry-message-input"
            rows={3}
            value={form.message}
            onChange={set("message")}
            data-testid="enquiry-message"
            className={inputCls}
            placeholder="Any preferences — budget, dietary, activities…"
          />
        </div>
      </div>

      <div className="mt-10 flex flex-col sm:flex-row gap-4">
        <button
          type="submit"
          data-testid="enquiry-submit"
          className="btn-primary"
        >
          <Send size={16} /> Send via WhatsApp
        </button>
        <button
          type="button"
          onClick={openMail}
          data-testid="enquiry-mailto"
          className={dark ? "btn-secondary" : "btn-ghost-dark"}
        >
          <Mail size={16} /> Email us instead
        </button>
      </div>
    </form>
  );
};
